import { BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxOfferApiGateway } from "@modules/boxOffer/gateway/boxOfferApi.gateway"
import {
  BoxOfferDetailedDto,
  BoxOfferInvitationDto,
  BoxOfferInvitationDtoSchema,
  BoxOfferInvitationInput,
  BoxOfferItemDto,
  BoxOfferItemDtoSchema,
  BoxOfferMobileDetailedDto,
  BoxOfferMobileDetailedDtoSchema,
  BoxSubscriptionOfferSchema,
  DropInOfferSchema,
  MilleniumOfferSchema,
  MobileBoxOfferItemDto,
  MobileBoxOfferItemDtoSchema,
  StatusFilter,
  TicketsBookOfferSchema,
  TrialOfferSchema,
} from "@modules/boxOffer/gateway/boxOfferDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ConfigService } from "@modules/core/services/configService"

export class OfferApi implements BoxOfferApiGateway {
  private baseUrl = "/box/offers"

  constructor(private api: ApiClient) {}

  async getBoxOffers(options?: { onlyWithPrice?: boolean; statusFilter?: StatusFilter }): Promise<BoxOfferItemDto[]> {
    const resultDto = await this.api.get<BoxOfferItemDto[]>(`${this.baseUrl}`, {
      params: {
        onlyWithPrice: options?.onlyWithPrice,
        statusFilter: options?.statusFilter,
      },
    })
    return resultDto.data.map((dto) => BoxOfferItemDtoSchema.parse(dto))
  }

  async getBoxOffersByUser(boxId: string): Promise<MobileBoxOfferItemDto[]> {
    const resultDto = await this.api.get<MobileBoxOfferItemDto[]>(`/boxes/${boxId}/offers`)

    return resultDto.data.map((dto) => MobileBoxOfferItemDtoSchema.parse(dto))
  }

  async getBoxOfferByUser(boxId: string, offerId: string): Promise<BoxOfferMobileDetailedDto> {
    const resultDto = await this.api.get<BoxOfferMobileDetailedDto>(`/boxes/${boxId}/offers/${offerId}`)

    return BoxOfferMobileDetailedDtoSchema.parse(resultDto.data)
  }

  async getBoxOffer(id: string): Promise<BoxOfferDetailedDto> {
    const resultDto = await this.api.get<BoxOfferDetailedDto>(`${this.baseUrl}/${id}`)

    switch (resultDto.data.type) {
      case BoxOfferType.Subscription:
        return BoxSubscriptionOfferSchema.parse(resultDto.data)
      case BoxOfferType.TicketsBook:
        return TicketsBookOfferSchema.parse(resultDto.data)
      case BoxOfferType.DropIn:
        return DropInOfferSchema.parse(resultDto.data)
      case BoxOfferType.Millenium:
        return MilleniumOfferSchema.parse(resultDto.data)
      case BoxOfferType.Trial:
        return TrialOfferSchema.parse(resultDto.data)
    }
  }

  async createBoxOffer(offerDraft: BoxOfferDraftEntity): Promise<void> {
    await this.api.post<BoxOfferDetailedDto>(`${this.baseUrl}`, BoxOfferDraftEntity.toDto(offerDraft))
  }

  async updateBoxOffer(id: string, offerDraft: BoxOfferDraftEntity): Promise<void> {
    await this.api.put<BoxOfferDetailedDto>(`${this.baseUrl}/${id}`, BoxOfferDraftEntity.toDto(offerDraft))
  }

  async deleteBoxOffer(id: string): Promise<void> {
    await this.api.post<BoxOfferDetailedDto>(`${this.baseUrl}/${id}/archive`)
  }

  async sendBoxOfferInvitation(boxOfferInvitationInput: BoxOfferInvitationInput): Promise<void> {
    await this.api.post(`${this.baseUrl}/invite`, boxOfferInvitationInput)
  }

  async getBoxOfferInvitations(userId: string): Promise<BoxOfferInvitationDto[]> {
    const resultDto = await this.api.get<BoxOfferInvitationDto[]>(`${this.baseUrl}/invitations/${userId}`)
    return resultDto.data.map((dto) => BoxOfferInvitationDtoSchema.parse(dto))
  }

  async cancelBoxOfferInvitation(invitationId: string): Promise<BoxOfferInvitationDto> {
    const resultDto = await this.api.post<BoxOfferInvitationDto>(`${this.baseUrl}/invitations/${invitationId}/cancel`)
    return BoxOfferInvitationDtoSchema.parse(resultDto.data)
  }

  async subscribeFree(boxId: string, offerId: string): Promise<void> {
    await this.api.post(`/boxes/${boxId}/offers/${offerId}/obtain`)
  }

  async createBoxOfferPaymentSubscription(
    offerId: string,
    successUrl: string,
    cancelUrl: string,
  ): Promise<{ url: string }> {
    const result = await this.api.post<{ url: string }>(
      `${ConfigService.config.BASE_URL.replace("/web", "")}/mobile/stripe/create-box-offer-checkout-session`,
      {
        boxOfferId: offerId,
        embedded: false,
        successUrl,
        cancelUrl,
      },
    )
    return result.data
  }

  async refuseOfferInvitation(boxOfferInvitationId: string) {
    const resultDto = await this.api.post<BoxOfferInvitationDto>(
      `${this.baseUrl}/invitations/${boxOfferInvitationId}/refuse`,
    )
    return BoxOfferInvitationDtoSchema.parse(resultDto.data)
  }
}
